import React, { useEffect, useState } from "react"
import { useMatch } from "react-router-dom"
import PropTypes from "prop-types"
import { oldRoutes } from "router/old-routes"
import { Button, Dropdown, Space, Layout, Segmented, Flex, Badge } from "antd"
import styles from "./TripFiltersNavigation.module.css"
import { DownOutlined, UnorderedListOutlined, EnvironmentOutlined, FilterOutlined } from "@ant-design/icons"
import { useScrollToActive } from "hooks/useScrollToActive"
import { useDispatch, useSelector } from "react-redux"
import {
  getFilterOptions,
  setPriceRange,
  setLanguage,
  setSkillLevel,
  setExperiences,
  setSubcategories,
  setOther,
  getPublicCategories,
  getPublicSubcategories,
  setView,
  clearAllFilters
} from "store/trips"
import { firstLoadingSelector } from "modules/loader-watchers/selectors"
import { useTranslation } from "react-i18next"
import PriceFilterDropdown from "components/filters/PriceFilterDropdown"
import LanguageFilterDropdown from "components/filters/LanguageFilterDropdown"
import SkillLevelFilterDropdown from "components/filters/SkillLevelFilterDropdown"
import ExperiencesFilterDropdown from "components/filters/ExperiencesFilterDropdown"
import OtherFilterDropdown from "components/filters/OtherFilterDropdown"
import FilterBadge from "components/FilterBadge"
import ClearFiltersButton from "components/common/ClearFiltersButton"
import useSearchFilter from "hooks/useSearchFilter"
import { TRIP_FILTERS } from "constants/trip"
import TripFiltersDrawer from "./TripFiltersDrawer"

export default function TripFiltersNavigation({ className, visible = false, fluid = true }) {
  const dispatch = useDispatch()
  const navigationRef = useScrollToActive()
  const isTripsPage = useMatch(oldRoutes.tripsPath())
  const filterOptionsFirstLoading = useSelector(firstLoadingSelector("trips.filter_options"))
  const { t } = useTranslation()
  const [categoriesData, setCategoriesData] = useState([])
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false)
  const [, updateSearchFilters] = useSearchFilter({ accessAttributes: TRIP_FILTERS })
  // selectors for filter state
  const priceRange = useSelector((state) => state.trips.priceRange)
  const language = useSelector((state) => state.trips.language)
  const skillLevel = useSelector((state) => state.trips.skillLevel)
  const experiences = useSelector((state) => state.trips.experiences)
  const subcategories = useSelector((state) => state.trips.subcategories)
  const subcategoriesByCategory = useSelector((state) => state.trips.publicSubcategories.byCategory)
  const other = useSelector((state) => state.trips.other)
  const view = useSelector((state) => state.trips.view)
  const filterOptions = useSelector((state) => state.trips?.filter_options?.lists || {})

  const classes = ["trip-filters-navigation", "ant-layout", "ant-layout-has-sider"]
  if (className) classes.push(className)

  useEffect(() => {
    if (filterOptionsFirstLoading) dispatch(getFilterOptions())
  }, [filterOptionsFirstLoading, dispatch]) //eslint-disable-line

  useEffect(() => {
    dispatch(getPublicCategories()).then((res) => {
      setCategoriesData(res)
    })
  }, [dispatch])

  // Lazy-load a category's subcategories the first time its accordion row is expanded
  const handleExpandCategory = (categoryId) => {
    if (!subcategoriesByCategory[categoryId]) {
      dispatch(getPublicSubcategories(categoryId))
    }
  }

  const priceLabel = priceRange?.length > 0 ? `${t("filterTags.price")} $${priceRange[0]} - $${priceRange[1]}` : t("filterTags.price")

  const absolutePriceRange = filterOptions?.price_range
  const priceFiltered =
    Array.isArray(priceRange) &&
    priceRange.length === 2 &&
    Array.isArray(absolutePriceRange) &&
    absolutePriceRange.length === 2 &&
    (priceRange[0] !== absolutePriceRange[0] || priceRange[1] !== absolutePriceRange[1])
  const hasFilters =
    priceFiltered ||
    language.length > 0 ||
    skillLevel.length > 0 ||
    experiences.length > 0 ||
    subcategories.length > 0 ||
    other.length > 0

  // Number of filter groups with an active selection — drives the mobile badge.
  const activeFilterCount = [
    priceFiltered,
    language.length > 0,
    skillLevel.length > 0,
    experiences.length + subcategories.length > 0,
    other.length > 0
  ].filter(Boolean).length

  // Build the filter panels once and share them between the desktop dropdowns
  // and the mobile drawer accordion so behaviour stays identical.
  const priceFilterPanel = <PriceFilterDropdown value={priceRange} onChange={(val) => dispatch(setPriceRange(val))} />
  const languageFilterPanel = (
    <LanguageFilterDropdown
      value={language}
      onChange={(val) => {
        dispatch(setLanguage(val))
        updateSearchFilters({ language: val })
      }}
    />
  )
  const skillLevelFilterPanel = <SkillLevelFilterDropdown value={skillLevel} onChange={(val) => dispatch(setSkillLevel(val))} />
  const experiencesFilterPanel = (
    <ExperiencesFilterDropdown
      value={experiences}
      onChange={(val) => dispatch(setExperiences(val))}
      categories={categoriesData}
      selectedSubcategories={subcategories}
      onSubcategoryChange={(val) => dispatch(setSubcategories(val))}
      subcategoriesByCategory={subcategoriesByCategory}
      onExpandCategory={handleExpandCategory}
      updateSearchFilters={updateSearchFilters}
    />
  )
  const otherFilterPanel = (
    <OtherFilterDropdown value={other} onChange={(val) => dispatch(setOther(val))} updateSearchFilters={updateSearchFilters} />
  )

  const filterSections = [
    { key: "price", label: t("filterTags.price"), count: priceFiltered ? 1 : 0, content: priceFilterPanel },
    { key: "language", label: t("filterTags.language"), count: language.length, content: languageFilterPanel },
    { key: "skill", label: t("filterTags.skill_level"), count: skillLevel.length, content: skillLevelFilterPanel },
    { key: "experiences", label: t("filterTags.experiences"), count: experiences.length + subcategories.length, content: experiencesFilterPanel },
    { key: "other", label: t("filterTags.other"), count: other.length, content: otherFilterPanel }
  ]

  return isTripsPage || visible ? (
    <Layout className={`${classes.join(" ")} ${styles.layoutWrapper}`}>
      <div ref={navigationRef} className="py-m w-full">
        <Flex gap={16} justify="space-between" align="center" className={styles.nowrapRow}>
          {/* Desktop: inline filter chips. Hidden on mobile in favour of the drawer. */}
          <div className={styles.filterWrapper}>
            <Space size="small" className="filterWrappers">
              <Dropdown overlay={priceFilterPanel} trigger={["click"]} key="Price">
                <Button shape="round" size="middle" className={styles.filterButton}>
                  {priceLabel} <DownOutlined />
                </Button>
              </Dropdown>
              <Dropdown overlay={languageFilterPanel} trigger={["click"]} key="Language">
                <Button shape="round" size="middle" className={styles.filterButton}>
                  <FilterBadge count={language.length} /> {t("filterTags.language")} <DownOutlined />
                </Button>
              </Dropdown>
              <Dropdown overlay={skillLevelFilterPanel} trigger={["click"]} key="Skill Level">
                <Button shape="round" size="middle" className={styles.filterButton}>
                  <FilterBadge count={skillLevel.length} /> {t("filterTags.skill_level")} <DownOutlined />
                </Button>
              </Dropdown>
              <Dropdown overlay={experiencesFilterPanel} trigger={["click"]} key="Experiences">
                <Button shape="round" size="middle" className={styles.filterButton}>
                  <FilterBadge count={experiences.length + subcategories.length} /> {t("filterTags.experiences")} <DownOutlined />
                </Button>
              </Dropdown>
              <Dropdown overlay={otherFilterPanel} trigger={["click"]} key="Other">
                <Button shape="round" size="middle" className={styles.filterButton}>
                  <FilterBadge count={other.length} /> {t("filterTags.other")} <DownOutlined />
                </Button>
              </Dropdown>
              {hasFilters && <ClearFiltersButton className={styles.clearFiltersButton} />}
            </Space>
          </div>
          {/* The List/Map toggle only controls the trips page map. The landing page
              has no map, so hide it there to avoid a Map tab that does nothing. */}
          {isTripsPage && (
            <div className={styles.viewToggle}>
              <Segmented
                value={view}
                onChange={(value) => dispatch(setView(value))}
                options={[
                  { label: t("filterTags.list_view"), value: "list", icon: <UnorderedListOutlined /> },
                  { label: t("filterTags.map_view"), value: "map", icon: <EnvironmentOutlined /> }
                ]}
              />
            </div>
          )}
          {/* Mobile: single entry point that opens the filters bottom drawer. */}
          <Badge count={activeFilterCount} color="#1976f6" className={styles.mobileFilterBadge}>
            <Button
              shape="round"
              size="middle"
              icon={<FilterOutlined />}
              className={styles.mobileFilterButton}
              onClick={() => setIsFilterDrawerOpen(true)}
              aria-label={t("filterTags.filters", "Filters")}
            />
          </Badge>
        </Flex>
        <TripFiltersDrawer
          open={isFilterDrawerOpen}
          setOpen={setIsFilterDrawerOpen}
          sections={filterSections}
          onClear={() => dispatch(clearAllFilters())}
          hasFilters={hasFilters}
        />
      </div>
    </Layout>
  ) : null
}

TripFiltersNavigation.propTypes = {
  className: PropTypes.string,
  visible: PropTypes.bool,
  fluid: PropTypes.bool
}
